import React, { useEffect, useState } from "react";
import { UserFormState, UserFormState_default } from "../../types/forms/UserFormState";
import Role from "../../types/Role";
import { getCompanyDepartmentOptionsAsync, getCompanyOptionsAsync } from "../../apiClient";
import { showDangerDialog } from "../../common";
import { CompanyOption } from "../../types/CompanyOption";
import { CompanyDepartmentOption } from "../../types/CompanyDepartmentOption";
import Resources from "../../types/Resources";

type Props = {
    resources: Resources;
    showNewUserRoleRowDefault: boolean;
}

function useUserForm(props: Props) {
    const [formState, setFormState] = useState<UserFormState>(UserFormState_default());
    const [isLoadingCompanyOptions, setIsLoadingCompanyOptions] = useState(true);
    const [companyDepartmentOptions, setCompanyDepartmentOptions] = useState<CompanyDepartmentOption[]>([]);
    const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
    const [showNewCompanyInputs, setShowNewCompanyInputs] = useState(false);
    const [showNewCompanyDepartmentInput, setShowNewCompanyDepartmentInput] = useState(false);
    const [showNewUserRoleRow, setShowNewUserRoleRow] = useState(props.showNewUserRoleRowDefault);

    const {resources} = props;

    useEffect(() => {
        async function fetchCompaniesAsync() {
            const result = await getCompanyOptionsAsync("");

            if (result.failed) {
                showDangerDialog(
                    resources.get("SomethingWentWrong"),
                    resources.get("RefreshPageAndTryAgain")
                );
                setIsLoadingCompanyOptions(false);
                return;
            }

            setCompanyOptions(result.value);
            setIsLoadingCompanyOptions(false);
        }

        fetchCompaniesAsync();
    }, []);

    useEffect(() => {
        async function fetchCompanyDepartmentsAsync(companyId: string) {
            const result = await getCompanyDepartmentOptionsAsync(companyId);

            if (result.failed) {
                showDangerDialog(
                    resources.get("SomethingWentWrong"),
                    resources.get("RefreshPageAndTryAgain")
                );
                return;
            }

            setCompanyDepartmentOptions(result.value);
        }

        if (formState.companyId.value) {
            fetchCompanyDepartmentsAsync(formState.companyId.value);
        } else {
            setCompanyDepartmentOptions([]);
            setFormState((prev) => ({
                ...prev,
                companyDepartmentId: { value: undefined, errorMessage: undefined }
            }));
        }
    }, [formState.companyId]);

    function handleFirstNameChange(newValue: string) {
        setFormState((prev) => ({ ...prev, firstName: { value: newValue, errorMessage: undefined } }));
    }

    function handleLastNameChange(newValue: string) {
        setFormState((prev) => ({ ...prev, lastName: { value: newValue, errorMessage: undefined } }));
    }

    function handleEmailChange(newValue: string) {
        setFormState((prev) => ({ ...prev, email: { value: newValue, errorMessage: undefined } }));
    }

    function handlePhoneNumberChange(newValue: string) {
        setFormState((prev) => ({ ...prev, phoneNumber: { value: newValue, errorMessage: undefined } }));
    }

    function handleReceivesSmsChange(newValue: boolean) {
        setFormState((prev) => ({ ...prev, receivesSms: { value: newValue, errorMessage: undefined } }));
    }

    function handleNewCompanyNameChange(newValue: string) {
        setFormState((prev) => ({ ...prev, newCompanyName: { value: newValue, errorMessage: undefined } }));
    }

    function handleNewDepartmentNameChange(newValue: string) {
        setFormState((prev) => ({ ...prev, newCompanyDepartmentName: { value: newValue, errorMessage: undefined } }));
    }

    function handleNewRoleAdded(newRole: Role) {
        setFormState((prev) => {
            const hasRole = prev.roles.value.some(
                (r) => r.roleCode === newRole.roleCode && r.departmentId === newRole.departmentId
            );
            if (hasRole) return prev;
            return { ...prev, roles: { value: [...prev.roles.value, newRole], errorMessage: undefined } };
        });
    }

    function handleRoleRemoved(roleToRemove: Role) {
        setFormState((prev) => ({
            ...prev,
            roles: {
                value: prev.roles.value.filter(
                    (r) => !(r.roleCode === roleToRemove.roleCode && r.departmentId === roleToRemove.departmentId)
                ),
                errorMessage: undefined
            }
        }));
    }

    async function handleCompanySearchAsync(searchText: string) {
        const result = await getCompanyOptionsAsync(searchText.trim());

        if (result.failed) {
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("RefreshPageAndTryAgain"));
            return;
        }

        setCompanyOptions(result.value);
    }

    function handleCompanySelected(_: CompanyOption[], selectedCompany: CompanyOption) {
        setFormState((prev) => ({
            ...prev,
            companyId: { value: selectedCompany.id, errorMessage: undefined },
            companyDepartmentId: { value: undefined, errorMessage: undefined }
        }));
        (document.getElementById("company-select_input") as HTMLInputElement | null)?.blur();
    }

    function handleCompanyRemoved(_: CompanyOption[], __: CompanyOption) {
        setFormState((prev) => ({
            ...prev,
            companyId: { value: "", errorMessage: undefined }
        }));
    }

    function handleCompanyDepartmentSelected(_: CompanyDepartmentOption[], selected: CompanyDepartmentOption) {
        setFormState((prev) => ({
            ...prev,
            companyDepartmentId: { value: selected.id, errorMessage: undefined }
        }));
        (document.getElementById("company-department-select_input") as HTMLInputElement | null)?.blur();
    }

    function handleCompanyDepartmentRemoved(_: CompanyDepartmentOption[], __: CompanyDepartmentOption) {
        setFormState((prev) => ({
            ...prev,
            companyDepartmentId: { value: undefined, errorMessage: undefined }
        }));
    }

    return {
        formState,
        setFormState,
        isLoadingCompanyOptions,
        companyDepartmentOptions,
        companyOptions,
        setCompanyOptions,
        showNewCompanyInputs,
        showNewCompanyDepartmentInput,
        showNewUserRoleRow,
        handleFirstNameChange,
        handleLastNameChange,
        handleEmailChange,
        handlePhoneNumberChange,
        handleReceivesSmsChange,
        handleNewCompanyNameChange,
        handleNewDepartmentNameChange,
        handleNewRoleAdded,
        handleRoleRemoved,
        handleCompanySearchAsync,
        handleCompanySelected,
        handleCompanyRemoved,
        handleCompanyDepartmentSelected,
        handleCompanyDepartmentRemoved,
        handleShowNewCompanyInputs: () => setShowNewCompanyInputs(true),
        handleHideNewCompanyInputs: () => setShowNewCompanyInputs(false),
        handleShowNewCompanyDepartmentInput: () => setShowNewCompanyDepartmentInput(true),
        handleHideNewCompanyDepartmentInput: () => setShowNewCompanyDepartmentInput(false),
        handleShowNewUserRoleRow: () => setShowNewUserRoleRow(true),
        handleHideNewUserRoleRow: () => setShowNewUserRoleRow(false)
    };
}

export default useUserForm;
